/**
 * 管理者アカウント回復 説明文（日本語）
 * セキュリティ画面の「アカウント回復の設定方法はこちら」から表示するモーダル用
 */

export const RECOVERY_INSTRUCTIONS_JA = `
## 1. アカウント回復とは

- **アカウント回復**は、レベル1管理者が**ログインパスワードを忘れた場合**に、本人確認をしてパスワードをリセットするための機能です。
- 通常の従業員コード・パスワードの代わりに、**回復用ID**と**回復用PIN**（セキュリティ画面で事前に設定）をログイン画面に入力すると、回復フローが始まります。
- 秘密の質問3つに正しく答えて新しいパスワードを設定すると、通常の従業員コードと新しいパスワードでログインできるようになります。
- **注意：** 回復後は**マスターキー（DEK）が自動では復元されません**。給与データを再度表示するには、回復後にセキュリティ画面でDEKを再入力する必要があります（セクション4参照）。

---

## 2. アカウント回復の設定方法

1. レベル1管理者としてログインします。
2. メニューから **「セキュリティ」** を開き、パスワードを入力してください。
3. **「管理者アカウントの回復設定」** までスクロールします。
4. 次の項目を入力してください。
   - **回復用ID** … 回復時にログイン画面の「従業員コード」欄に入力するコード（例：RECOVERY01）。通常の従業員コードとは別の、任意の文字列でかまいません。
   - **回復用PIN** … 回復時にログイン画面の「パスワード」欄に入力するコード。他人に推測されにくいものにし、秘密に保管してください。
   - **秘密の質問3つとその回答** … ご自身で質問と回答を自由に設定します（例：「母親の旧姓は？」「初めて飼ったペットの名前は？」）。回復時に本人確認に使われます。
5. **「回復設定を保存する」** をクリックして保存します。
6. **回復用IDと回復用PINは必ず安全な場所に保管してください**（セクション3参照）。パスワードを忘れたときに必要になります。

設定の変更は、同じセクションの **「設定を変更する」** からいつでも行えます。

---

## 3. 回復用ID・PINを安全に保管する重要性

- **なぜ重要か**  
  ログインパスワードを忘れた場合、**回復用ID**と**回復用PIN**がないと回復フローを開始できません。紛失すると管理者アカウントに再ログインできなくなる可能性があります。

- **推奨する保管方法**  
  - 紙にメモして、金庫や鍵付きキャビネットなど、**第三者に見られない安全な場所**に保管してください。  
  - **通常のログインパスワードと同じ場所**（同じメモやファイル）に書かないでください。  
  - メールで送信したり、共有フォルダやパソコンに平文で保存したりしないでください。

- **機密性**  
  回復用IDとPINを知っている人は、秘密の質問に答えることで新しいパスワードを設定し、管理者としてログインできます。**管理者アカウントの「予備の鍵」**として扱い、厳重に管理してください。

---

## 4. アカウント回復を行ったあと ― マスターキー（DEK）の再入力

- **何が起きるか**  
  回復フローでパスワードをリセットすると、システムは**古いパスワードを知りません**。そのため、古いパスワードで暗号化して保存していた**マスターキー（DEK）**を自動では復元できません。アカウントに紐づいていたDEKは解除され、**DEKを再度設定するまで給与・賞与の金額データは表示されません**。

- **必要な操作**  
  1. **通常の従業員コード**と、回復時に設定した**新しいパスワード**でログインします。  
  2. **「セキュリティ」** を開き、求められたらパスワードを再入力します。  
  3. マスターキー（DEK）の項目で **「マスターキーを設定する」**（初回設定と同じ操作）を選びます。  
  4. 回復前に使用していた**同じマスターキー（DEK）**を、安全な場所に保管していたものから入力して設定します。  
  5. 設定を保存すると、そのDEKで暗号化された給与・賞与データが再び表示されるようになります。

- **DEKを紛失している場合**  
  DEKを保管しておらず、内容が分からなくなっている場合は、既存の暗号化済み給与データを復号できません。その場合は**新しいDEK**を設定し、マスターキーリセット手順に従ってデータの再暗号化などを行う必要があります。そのため、**DEKは初回設定時に必ず安全な場所に保管し、回復後も同じDEKを再入力できるようにしておいてください。**

---

## 5. クイックリファレンス

| 項目 | 内容 |
|------|------|
| 回復用ID・PIN | セキュリティ → 管理者アカウントの回復設定で設定。**パスワードを忘れたとき**は、ログイン画面の「従業員コード」「パスワード」欄に入力して回復を開始。 |
| 秘密の質問（3つ） | 同じ回復設定で設定。回復フローで本人確認に使用。 |
| マスターキー（DEK） | セキュリティで設定・リセット。**回復後は同じDEKを再入力**し、給与データを再度表示可能にする。 |
| 安全な保管 | 回復用ID・回復用PIN・DEKはすべて**安全で他人の目に触れない場所**に保管し、共有や不用意な保存を避ける。 |
`;
